import { useRef, useState } from 'react'
import * as React from 'react'
import cx from 'classnames'
import { Formik, FormikProps } from 'formik'
import { camelCase, invert, isEqual, pick } from 'lodash'

import { Button } from 'components/Button'
import Styles from './TextStyleModal.module.scss'
import { ArticleStyle, ArticleStyleProperty, RootState } from 'types'
import { LogoLoader } from 'components/LogoLoader/LogoLoader'
import { PreviewNodes } from 'components/Settings/ArticleStyle/constants'
import { ProsePreview } from 'common/ProsePreview/ProsePreview'
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevronRight16.svg'
import { ReactComponent as DocCheckmarkIcon } from 'assets/icons/docCheckmark24.svg'
import { StyleSectionForm } from 'components/Settings/ArticleStyle/StyleSection/StyleSectionForm'
import { TEXT_STYLES } from 'common/Prose/constants'
import { schema } from 'prose-core'
import { useComputedEditorStyles } from 'components/Settings/ArticleStyle/useComputedEditorStyles'
import { useLoading } from 'hooks/UseLoading'
import { Modal, useModals } from 'almanac-ui/Modal'
import { useDispatch, useSelector } from 'react-redux'
import { useWorkspace } from 'hooks/useWorkspace'
import { ArticleService } from 'api/services/ArticleService'
import { handleResponseWithMessage } from 'api/utils/handleResponseWithMessage'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { articleMetaUpsert } from 'redux/actions'
import { Reset } from 'almanac-ui/icons/16'

const ARTICLE_STYLE_UPDATE_SUCCESS_MESSAGE = 'Your doc styles have been updated.'
const WORKSPACE_ARTICLE_STYLE_UPDATE_SUCCESS_MESSAGE = 'Your workspace styles have been updated.'

const TextStyleArticleStyleKeys: { [key in keyof typeof TEXT_STYLES]: keyof ArticleStyle } = {
  normal: 'body',
  title: 'h1',
  heading1: 'h2',
  heading2: 'h3',
  heading3: 'h4',
}

const ArticleStyleTextStyleKeys = invert(TextStyleArticleStyleKeys)

const TextStyleTagName: { [key in keyof typeof TEXT_STYLES]: keyof ReturnType<typeof useComputedEditorStyles> } = {
  normal: 'p',
  title: 'h1',
  heading1: 'h2',
  heading2: 'h3',
  heading3: 'h4',
}

const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Are you sure you want to discard them?'
const getResetConfirmMessage = (key: keyof ArticleStyle) =>
  `Are you sure you want to reset the styles for "${TEXT_STYLES[ArticleStyleTextStyleKeys[key]].textValue}"?`

const camelizeKeys = (obj: any) =>
  Object.entries(obj).reduce((convertedObj, [key, value]) => ({ ...convertedObj, [camelCase(key)]: value }), {})

const buildStyleObjectFromArticleStyleProperty = (articleStyleProperty: ArticleStyleProperty) =>
  pick(camelizeKeys(articleStyleProperty), [
    'color',
    'fontFamily',
    'fontStyle',
    'fontWeight',
    'textDecoration',
    'textTransform',
  ])

export const DefaultArticleStyle: ArticleStyle = {
  body: {},
  blockquote: {},
  callout: {},
  h1: {},
  h2: {},
  h3: {},
  h4: {},
}

export const TextStyleModal = () => {
  const dispatch = useDispatch()
  const { closeModal } = useModals()
  const articleMeta = useSelector(({ articleMeta, currentArticleId }: RootState) =>
    currentArticleId ? articleMeta[currentArticleId] : null,
  )
  const workspace = useWorkspace(articleMeta?.company_id || null)
  const articleStyle = articleMeta?.article_style || DefaultArticleStyle
  const [activeStyleKey, setActiveStyleKey] = useState<keyof typeof TEXT_STYLES>('normal')
  const [wrappedOnReset, resetLoading] = useLoading(async (key: keyof ArticleStyle) => {
    if (window.confirm(getResetConfirmMessage(key))) await updateArticleStyle({ ...articleStyle, [key]: {} })
  })
  const defaultPreviewRef = useRef<HTMLDivElement>(null)
  const [defaultsPreviewInitialized, setDefaultsPreviewInitialized] = useState(false)
  const defaultComputedStyles = useComputedEditorStyles(defaultPreviewRef.current)
  const formRef = useRef<FormikProps<ArticleStyle>>(null)

  // get styles for the tab buttons from the preview
  const articleStylePreviewRef = useRef<HTMLDivElement>(null)
  const articleStyleComputedStyles = useComputedEditorStyles(articleStylePreviewRef.current, [
    activeStyleKey,
    formRef.current?.values,
  ])
  const [previewInitialized, setPreviewInitialized] = useState(false)

  const updateArticleStyle = async (articleStyle: ArticleStyle) => {
    if (!articleMeta) return Promise.reject()

    await ArticleService.updateArticle(articleMeta.id, { article_style_attributes: articleStyle }).then(
      handleResponseWithMessage({ successMessage: ARTICLE_STYLE_UPDATE_SUCCESS_MESSAGE }),
    )
  }

  const applyToWorkspace = async (articleStyle: ArticleStyle) => {
    if (!workspace || !articleMeta) return Promise.reject()

    // merge workspace-wide and per-doc into one to not lose non-overwritten values
    const mergedArticleStyle = Object.entries(articleMeta.workspace_article_style || DefaultArticleStyle).reduce(
      (merged, [key, property]) => ({
        ...merged,
        [key]: { ...property, ...articleStyle[key as keyof ArticleStyle] },
      }),
      {},
    )

    const response = await client
      .updateWorkspaceSettings({ workspace: { article_style_attributes: mergedArticleStyle } }, workspace.key)
      .then(handleResponseWithMessage({ successMessage: WORKSPACE_ARTICLE_STYLE_UPDATE_SUCCESS_MESSAGE }))

    if (response?.type === SUCCESS_RESPONSE_TYPE) {
      dispatch(articleMetaUpsert({ workspace_article_style: response.data.company.article_style }))
    }
  }

  const [wrappedOnApplyToWorkspace, applyToWorkspaceLoading] = useLoading(applyToWorkspace)
  const [wrappedOnUpdate, updateLoading] = useLoading(updateArticleStyle)

  const pristine = (values: ArticleStyle) =>
    isEqual(values[TextStyleArticleStyleKeys[activeStyleKey]], articleStyle[TextStyleArticleStyleKeys[activeStyleKey]])

  const handleTabChange =
    (key: typeof activeStyleKey, values: ArticleStyle, resetForm: () => void) => (event: React.MouseEvent) => {
      event.stopPropagation()

      if (pristine(values)) {
        setActiveStyleKey(key)
      } else {
        if (window.confirm(UNSAVED_CHANGES_MESSAGE)) {
          resetForm()
          setTimeout(() => setActiveStyleKey(key)) // wait for preview to determine styles
        }
      }
    }

  const handleClose = () => {
    if (!formRef.current?.values) closeModal()

    if (formRef.current?.values && pristine(formRef.current?.values)) {
      closeModal()
    } else {
      if (window.confirm(UNSAVED_CHANGES_MESSAGE)) {
        closeModal()
      }
    }
  }

  return (
    <Modal.Root onPressOutside={handleClose} size="lg">
      <Modal.Header
        title="Doc Text Styles"
        actions={
          <div className={Styles.modalHeader}>
            {!!workspace?.admin && (
              <Button
                isDisabled={applyToWorkspaceLoading}
                frontIcon={DocCheckmarkIcon}
                onPress={() => wrappedOnApplyToWorkspace(articleStyle)}
                variant="tertiary"
                size="lg"
              >
                Apply to workspace styles
              </Button>
            )}
          </div>
        }
      />
      <Modal.Content>
        <Formik enableReinitialize initialValues={articleStyle} innerRef={formRef} onSubmit={wrappedOnUpdate}>
          {({ handleSubmit, setFieldValue, setValues, values, resetForm }) => (
            <div className={Styles.container}>
              <ul className={Styles.textStyleList}>
                {previewInitialized &&
                  Object.entries(TEXT_STYLES).map(([key, { textValue }]) => (
                    <li className={cx({ [Styles.active]: activeStyleKey === key })} key={key}>
                      <button
                        onClick={handleTabChange(key, values, resetForm)}
                        style={buildStyleObjectFromArticleStyleProperty(
                          articleStyleComputedStyles[TextStyleTagName[key]],
                        )}
                      >
                        <span className={Styles.text}>{textValue}</span>
                        <ChevronRightIcon className={Styles.chevron} />
                      </button>
                    </li>
                  ))}
              </ul>

              <div className={Styles.detailsContainer}>
                <div className={Styles.defaultsPreview} ref={defaultPreviewRef}>
                  <ProsePreview
                    articleStyles={[articleMeta?.workspace_article_style]}
                    nodes={PreviewNodes}
                    onInit={() => setDefaultsPreviewInitialized(true)}
                    schema={schema}
                  />
                </div>

                {!defaultsPreviewInitialized && (
                  <div className={Styles.loader}>
                    <LogoLoader />
                  </div>
                )}

                {defaultsPreviewInitialized && (
                  <form onSubmit={handleSubmit}>
                    <div className={Styles.configuration}>
                      <h2>Font Configuration</h2>
                      <StyleSectionForm
                        defaults={defaultComputedStyles[TextStyleTagName[activeStyleKey]]}
                        name={TextStyleArticleStyleKeys[activeStyleKey]}
                        property={values[TextStyleArticleStyleKeys[activeStyleKey]]}
                        setFieldValue={setFieldValue}
                      />
                      <div className={Styles.buttons}>
                        <Button
                          type="button"
                          isDisabled={
                            isEqual(articleStyle[TextStyleArticleStyleKeys[activeStyleKey]], {}) || resetLoading
                          }
                          frontIcon={Reset}
                          onClick={() => wrappedOnReset(TextStyleArticleStyleKeys[activeStyleKey])}
                          size="lg"
                          variant="tertiary"
                        >
                          Reset
                        </Button>
                        <Button
                          type="submit"
                          className={Styles.updateButton}
                          isDisabled={pristine(values) || updateLoading}
                          size="lg"
                        >
                          Update
                        </Button>
                      </div>
                    </div>

                    <div className={Styles.previewContainer}>
                      <header>Preview</header>
                      {!previewInitialized && (
                        <div className={Styles.loader}>
                          <LogoLoader />
                        </div>
                      )}
                      <div ref={articleStylePreviewRef}>
                        <ProsePreview
                          articleStyles={[articleMeta?.workspace_article_style, values]}
                          nodes={PreviewNodes}
                          onInit={() => setPreviewInitialized(true)}
                          schema={schema}
                        />
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  )
}
